import axios from 'axios';
import configService from '@/services/configService';

function apiUrl() {
  return configService.get('apiUrl');
}

function unwrap(response) {
  return response.data;
}

export default {
  // ─── Team conversations ───────────────────────────────────────────────
  listConversations(params = {}) {
    return axios.get(`${apiUrl()}/chat/conversations`, { params }).then(unwrap);
  },

  createConversation(params) {
    return axios.post(`${apiUrl()}/chat/conversations`, params).then(unwrap);
  },

  updateConversation(conversationId, params) {
    return axios.patch(`${apiUrl()}/chat/conversations/${conversationId}`, params).then(unwrap);
  },

  deleteConversation(conversationId) {
    return axios.delete(`${apiUrl()}/chat/conversations/${conversationId}`).then(unwrap);
  },

  leaveConversation(conversationId) {
    return axios.post(`${apiUrl()}/chat/conversations/${conversationId}/leave`).then(unwrap);
  },

  hideConversation(conversationId) {
    return axios.post(`${apiUrl()}/chat/conversations/${conversationId}/hide`).then(unwrap);
  },

  muteConversation(conversationId, { muted = true, mutedUntil = null } = {}) {
    return axios
      .patch(`${apiUrl()}/chat/conversations/${conversationId}/mute`, { muted, mutedUntil })
      .then(unwrap);
  },

  pinConversation(conversationId, { pinned = true } = {}) {
    return axios
      .patch(`${apiUrl()}/chat/conversations/${conversationId}/pin`, { pinned })
      .then(unwrap);
  },

  nudge(conversationId, { targetUserId, messageId } = {}) {
    return axios
      .post(`${apiUrl()}/chat/conversations/${conversationId}/nudge`, {
        targetUserId,
        messageId: messageId || undefined
      })
      .then(unwrap);
  },

  notify(conversationId) {
    return axios.post(`${apiUrl()}/chat/conversations/${conversationId}/notify`).then(unwrap);
  },

  // ─── Messages ─────────────────────────────────────────────────────────
  listMessages(conversationId, params = {}) {
    return axios
      .get(`${apiUrl()}/chat/conversations/${conversationId}/messages`, { params })
      .then(unwrap);
  },

  sendMessage(conversationId, params) {
    return axios
      .post(`${apiUrl()}/chat/conversations/${conversationId}/messages`, params)
      .then(unwrap);
  },

  updateMessage(conversationId, messageId, params) {
    return axios
      .patch(`${apiUrl()}/chat/conversations/${conversationId}/messages/${messageId}`, params)
      .then(unwrap);
  },

  deleteMessage(conversationId, messageId) {
    return axios
      .delete(`${apiUrl()}/chat/conversations/${conversationId}/messages/${messageId}`)
      .then(unwrap);
  },

  markMessageRead(conversationId, messageId) {
    return axios
      .post(`${apiUrl()}/chat/conversations/${conversationId}/messages/${messageId}/read`)
      .then(unwrap);
  },

  // ─── Participants ─────────────────────────────────────────────────────
  addParticipant(conversationId, params) {
    return axios
      .post(`${apiUrl()}/chat/conversations/${conversationId}/participants`, params)
      .then(unwrap);
  },

  removeParticipant(conversationId, userId) {
    return axios
      .delete(`${apiUrl()}/chat/conversations/${conversationId}/participants/${userId}`)
      .then(unwrap);
  },

  updateParticipantRole(conversationId, userId, { role }) {
    return axios
      .patch(`${apiUrl()}/chat/conversations/${conversationId}/participants/${userId}`, { role })
      .then(unwrap);
  },

  searchParticipants(conversationId, query = '') {
    return axios
      .get(`${apiUrl()}/chat/conversations/${conversationId}/participants/search`, {
        params: { q: query }
      })
      .then(unwrap);
  },

  listMentions(params = {}) {
    return axios.get(`${apiUrl()}/chat/mentions`, { params }).then(unwrap);
  },

  // ─── AI chat ──────────────────────────────────────────────────────────
  sendAiMessage({ conversationId, message }) {
    return axios
      .post(`${apiUrl()}/ai-chat/message`, {
        conversationId: conversationId || undefined,
        message
      })
      .then(unwrap);
  },

  listAiConversations(params = {}) {
    return axios.get(`${apiUrl()}/ai-chat/conversations`, { params }).then(unwrap);
  },

  getAiConversation(conversationId) {
    return axios.get(`${apiUrl()}/ai-chat/conversations/${conversationId}`).then(unwrap);
  },

  deleteAiConversation(conversationId) {
    return axios.delete(`${apiUrl()}/ai-chat/conversations/${conversationId}`).then(unwrap);
  },

  // ─── Files ────────────────────────────────────────────────────────────
  async uploadChatFiles(files = []) {
    if (!files || files.length === 0) return [];

    const filesArray = Array.from(files);
    const uploaded = await Promise.all(
      filesArray.map(async file => {
        const response = await axios.post(`${apiUrl()}/files/upload`, {
          fileName: file.name,
          fileSize: file.size,
          subDirectory: 'chat'
        });
        const data = response.data?.data || response.data;
        const uploadSignedUrl = data?.uploadSignedUrl;
        const photoPath = data?.file?.id || '';

        if (!uploadSignedUrl) {
          throw new Error('Failed to get signed URL.');
        }

        const instance = axios.create();
        delete instance.defaults.headers.common.Authorization;

        await instance({
          method: 'put',
          url: uploadSignedUrl,
          headers: { 'Content-Type': file.type },
          data: file
        });

        return { photoPath, fileName: file.name, response: data };
      })
    );

    return uploaded;
  },

  // ─── Users (for DM / group pickers) ───────────────────────────────────
  listUsers(params = {}) {
    return axios.get(`${apiUrl()}/users`, { params }).then(unwrap).catch(() =>
      // Fallback to legacy Disha user list
      axios
        .get(`${apiUrl()}/user`, {
          params: {
            page: params.page || 1,
            page_size: params.limit || params.page_size || 1000,
            q: params.q || params.search || undefined
          }
        })
        .then(unwrap)
    );
  }
};
